<script setup lang="ts">
// Modal / dialog (Plan 1 `ui.js` `TDS.modal`). Controlled via `open` (v-model:open); parent decides
// whether `@ok` actually closes the dialog (e.g. to keep it open after a validation error).
import { onBeforeUnmount, onMounted } from 'vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    size?: 'md' | 'lg' | 'xl'
    showFoot?: boolean
    showCancel?: boolean
    cancelText?: string
    okText?: string
    danger?: boolean
    closeOnOverlay?: boolean
  }>(),
  {
    title: 'Dialog',
    size: 'md',
    showFoot: true,
    showCancel: true,
    cancelText: 'Cancel',
    okText: undefined,
    closeOnOverlay: true,
  },
)

const emit = defineEmits<{ 'update:open': [boolean]; ok: []; close: [] }>()

function close(): void {
  emit('update:open', false)
  emit('close')
}
function onOverlayMousedown(e: MouseEvent): void {
  if (props.closeOnOverlay && e.target === e.currentTarget) close()
}
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && props.open) close()
}
onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="overlay" role="dialog" @mousedown="onOverlayMousedown">
      <div class="modal" :class="size !== 'md' ? `modal-${size}` : ''">
        <div class="modal-head">
          <h2>{{ title }}</h2>
          <button class="btn btn-icon" type="button" aria-label="Close" @click="close">×</button>
        </div>
        <div class="modal-body">
          <slot />
        </div>
        <div v-if="showFoot" class="modal-foot">
          <span><slot name="footer-left" /></span>
          <div style="display: flex; gap: 8px">
            <button v-if="showCancel" class="btn" type="button" @click="close">
              {{ cancelText }}
            </button>
            <button
              v-if="okText"
              class="btn"
              :class="danger ? 'btn-danger' : 'btn-primary'"
              type="button"
              @click="emit('ok')"
            >
              {{ okText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
